import { createContext, useState, useMemo, useEffect } from "react";
import { createTheme } from "@mui/material/styles";

export const themes = {
  light: {
    palette: {
      mode: "light",
      common: {
        black: "#282828",
        white: "#fff",
      },
      primary: {
        main: "#e90000", //custom color
        light: "#951914", //custom color
        dark: "#951914", //custom color
        contrastText: "#fff",
      },
      secondary: {
        main: "#fcfcfc", //custom color
        light: "#ba68c8",
        dark: "#f3e1da", //custom color
        contrastText: "#fff",
      },
      error: {
        main: "#d32f2f",
        light: "#f48c8a",
        dark: "#c62828",
        contrastText: "#fff",
      },
      warning: {
        main: "#ed6c02",
        light: "#ff9800",
        dark: "#e65100",
        contrastText: "#fff",
      },
      info: {
        main: "#0288d1",
        light: "#03a9f4",
        dark: "#01579b",
        contrastText: "#fff",
      },
      success: {
        main: "#2e7d32",
        light: "#4caf50",
        dark: "#1b5e20",
        contrastText: "#fff",
      },
      grey: {
        50: "#fafafa",
        100: "#f5f5f5",
        200: "#eeeeee",
        300: "#e0e0e0",
        400: "#bdbdbd",
        500: "#9e9e9e",
        600: "#757575",
        700: "#616161",
        800: "#424242",
        900: "#212121",
        1000: "#f5f5f5",
        A100: "#f5f5f5",
        A200: "#eeeeee",
        A400: "#bdbdbd",
        A700: "#616161",
      },
      contrastThreshold: 3,
      tonalOffset: 0.2,
      text: {
        primary: "rgba(0, 0, 0, 0.87)",
        secondary: "rgba(0, 0, 0, 0.6)",
        disabled: "rgba(0, 0, 0, 0.38)",
      },
      divider: "rgba(0, 0, 0, 0.12)",
      background: {
        paper: "#FCFCFC", //custom color
        default: "#F2EBE2", //custom color
      },
      action: {
        active: "rgba(0, 0, 0, 0.54)",
        hover: "rgba(0, 0, 0, 0.04)",
        hoverOpacity: 0.04,
        selected: "rgba(0, 0, 0, 0.08)",
        selectedOpacity: 0.08,
        disabled: "rgba(0, 0, 0, 0.26)",
        disabledBackground: "rgba(0, 0, 0, 0.12)",
        disabledOpacity: 0.38,
        focus: "rgba(0, 0, 0, 0.12)",
        focusOpacity: 0.12,
        activatedOpacity: 0.12,
      },
      custom: {
        gradient:
          "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 40%, rgba(255, 255, 255, 0.5) 70%, rgba(255, 255, 255, 0.9) 90%, rgba(255, 255, 255, 1) 100%)",
      },
    },
  },
  dark: {
    palette: {
      mode: "dark",
      common: {
        black: "#282828",
        white: "#fff",
      },
      primary: {
        main: "#e90000", //custom color
        light: "#e3f2fd",
        dark: "#951914", //custom color
        contrastText: "#fff", //custom color
      },
      secondary: {
        main: "#1c1c1c", //custom color
        light: "#f3e5f5",
        dark: "#1c1c1c", //custom color
        contrastText: "rgba(0, 0, 0, 0.87)",
      },
      error: {
        main: "#f44336",
        light: "#f48c8a",
        dark: "#d32f2f",
        contrastText: "#fff",
      },
      warning: {
        main: "#ffa726",
        light: "#ffb74d",
        dark: "#f57c00",
        contrastText: "rgba(0, 0, 0, 0.87)",
      },
      info: {
        main: "#29b6f6",
        light: "#4fc3f7",
        dark: "#0288d1",
        contrastText: "rgba(0, 0, 0, 0.87)",
      },
      success: {
        main: "#66bb6a",
        light: "#81c784",
        dark: "#388e3c",
        contrastText: "rgba(0, 0, 0, 0.87)",
      },
      grey: {
        50: "#fafafa",
        100: "#f5f5f5",
        200: "#eeeeee",
        300: "#e0e0e0",
        400: "#bdbdbd",
        500: "#9e9e9e",
        600: "#424242",
        800: "#757575",
        700: "#616161",
        900: "#212121",
        1000: "#343434",
        A100: "#f5f5f5",
        A200: "#eeeeee",
        A400: "#bdbdbd",
        A700: "#616161",
      },
      contrastThreshold: 3,
      tonalOffset: 0.2,
      text: {
        primary: "#fcfcfc", //custom color
        secondary: "rgba(255, 255, 255, 0.7)",
        disabled: "rgba(255, 255, 255, 0.5)",
        icon: "rgba(255, 255, 255, 0.5)",
      },
      divider: "rgba(255, 255, 255, 0.12)",
      background: {
        paper: "#1c1c1c", //custom color
        default: "#232323", //custom color
      },
      action: {
        active: "#fff",
        hover: "rgba(255, 255, 255, 0.08)",
        hoverOpacity: 0.08,
        selected: "rgba(255, 255, 255, 0.16)",
        selectedOpacity: 0.16,
        disabled: "rgba(255, 255, 255, 0.3)",
        disabledBackground: "rgba(255, 255, 255, 0.12)",
        disabledOpacity: 0.38,
        focus: "rgba(255, 255, 255, 0.12)",
        focusOpacity: 0.12,
        activatedOpacity: 0.24,
      },
      custom: {
        gradient:
          "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(28, 28, 28, 0.2) 40%, rgba(28, 28, 28, 0.5) 70%, rgba(28, 28, 28, 0.9) 90%, rgba(28, 28, 28, 1) 100%)", 
      },
    },
  },
};

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

// Custom hook to manage color mode
export const useMode = () => {
  const storedMode = localStorage.getItem("freecomers_theme") || "light";
  const [mode, setMode] = useState(storedMode);

  const theme = useMemo(
    () =>
      createTheme({
        ...themes[mode],
        typography: {
          fontFamily: "Roboto Condensed, sans-serif",
        },
      }),
    [mode]
  );

  useEffect(() => {
    localStorage.setItem("freecomers_theme", mode);
  }, [mode]);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  return [theme, { toggleColorMode }];
};
